import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  REPORTS_ENABLED,
  SALES_REGIONS,
  SalesAgent,
  SalesAgentCandidate,
  createSalesAgent,
  fetchAgentCandidates,
  fetchSalesAgents,
  importSalesAgents,
  updateSalesAgent,
} from '../../../Reports/reportsApi';
import {
  Page,
  PageHeader,
  Card,
  Button,
  Badge,
  Field,
  TextInput,
  Select,
  Table,
  Loader,
  EmptyState,
} from '../../../ui';
import './sales.scss';

/**
 * Sales → Sales Agents (SALES_ADMIN).
 *
 * Who the sales agents are. An agent is keyed by the email they have in
 * Salesforce, which is also their CMS login: it's how their leads are found
 * and how their own report is scoped. Targets and incentive plans for these
 * agents are on Sales → Targets & Incentives.
 */

const num = (n: number): string => Math.round(n).toLocaleString('en-IN');
const errMsg = (err: any, fallback: string): string => err?.message || fallback;

const AgentsTab: React.FC = () => {
  const [agents, setAgents] = useState<SalesAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState<SalesAgentCandidate[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', region: '', team: '' });
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState({ email: '', name: '', region: '', team: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAgents(await fetchSalesAgents());
    } catch (err) {
      toast.error(errMsg(err, 'Could not load agents'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const loadCandidates = async () => {
    setBusy(true);
    try {
      const list = await fetchAgentCandidates(true);
      setCandidates(list);
      setPicked(new Set(list.map((c) => c.email)));
    } catch (err) {
      toast.error(errMsg(err, 'Could not read Salesforce lead owners'));
    } finally {
      setBusy(false);
    }
  };

  const doImport = async () => {
    setBusy(true);
    try {
      const res = await importSalesAgents(Array.from(picked));
      toast.success(`Added ${res.created.length} agent${res.created.length === 1 ? '' : 's'} from Salesforce`);
      setCandidates(null);
      void load();
    } catch (err) {
      toast.error(errMsg(err, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createSalesAgent({ email: form.email, name: form.name, region: form.region || null, team: form.team || null });
      toast.success(`${form.name} added`);
      setForm({ email: '', name: '', region: '', team: '' });
      void load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not add the agent'));
    }
  };

  const save = async (agent: SalesAgent, body: Parameters<typeof updateSalesAgent>[1]) => {
    try {
      await updateSalesAgent(agent.id, body);
      setEditing(null);
      void load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not save'));
    }
  };

  const regionOptions = (empty: string) => [
    { value: '', label: empty },
    ...SALES_REGIONS.map((r) => ({ value: r, label: r })),
  ];

  return (
    <div className="sales-agents">
      <div className="sales-agents__side">
        <Card
          title="Import from Salesforce"
          subtitle="Lead owners with completed demos in the last 90 days who aren't agents here yet."
        >
          {candidates === null ? (
            <Button variant="secondary" size="sm" loading={busy} onClick={() => void loadCandidates()}>
              Find lead owners
            </Button>
          ) : candidates.length === 0 ? (
            <EmptyState title="No new lead owners" description="Everyone active in Salesforce is already an agent." />
          ) : (
            <div className="ds-stack">
              <div className="sales-picklist">
                {candidates.map((c) => (
                  <label key={c.email} className="sales-picklist__item">
                    <input type="checkbox" checked={picked.has(c.email)}
                      onChange={(e) => setPicked((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(c.email); else next.delete(c.email);
                        return next;
                      })} />
                    <span className="sales-picklist__who">
                      <span className="ds-strong">{c.name || c.email}</span>
                      <span className="sales-caption ds-text-muted">{c.email}</span>
                    </span>
                    <span className="sales-picklist__meta sales-caption ds-text-muted">
                      {c.region ?? '—'}<br />{num(c.recentDemos)} demos
                    </span>
                  </label>
                ))}
              </div>
              <div className="sales-actions">
                <Button variant="accent" size="sm" disabled={!picked.size || busy} onClick={() => void doImport()}>
                  Add {picked.size} agent{picked.size === 1 ? '' : 's'}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setCandidates(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </Card>

        <Card
          title="Add an agent"
          subtitle="Use the email they have in Salesforce — it's how their leads are found, and their CMS login."
        >
          <form onSubmit={(e) => void add(e)} className="ds-stack">
            <Field label="Salesforce email" required>
              {(ctl) => (
                <TextInput {...ctl} type="email" required placeholder="email@artiumacademy.com" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              )}
            </Field>
            <Field label="Full name" required>
              {(ctl) => (
                <TextInput {...ctl} required placeholder="Full name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              )}
            </Field>
            <div className="sales-form-row">
              <Field label="Region">
                {(ctl) => (
                  <Select {...ctl} options={regionOptions('Region…')} value={form.region}
                    onChange={(e) => setForm({ ...form, region: e.target.value })} />
                )}
              </Field>
              <Field label="Team / pod">
                {(ctl) => (
                  <TextInput {...ctl} placeholder="Team / pod" value={form.team}
                    onChange={(e) => setForm({ ...form, team: e.target.value })} />
                )}
              </Field>
            </div>
            <Button variant="primary" type="submit">Add agent</Button>
          </form>
        </Card>
      </div>

      <div className="sales-agents__main">
        <Card
          flush
          title={<>Sales agents <span className="ds-text-muted">· {agents.filter((a) => a.isActive).length} active</span></>}
        >
          {loading ? (
            <Loader label="Loading agents…" />
          ) : agents.length === 0 ? (
            <EmptyState title="No agents yet" description="No agents yet. Import them from Salesforce or add one." />
          ) : (
            <Table caption="Sales agents">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Region</th>
                  <th>Team</th>
                  <th>Status</th>
                  <th><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => editing === a.id ? (
                  <tr key={a.id}>
                    <td>
                      <div className="sales-cell-stack">
                        <TextInput size="sm" value={edit.name} placeholder="Name" aria-label="Name"
                          onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                        <TextInput size="sm" type="email" value={edit.email} placeholder="Salesforce email"
                          aria-label="Salesforce email"
                          title="Must match their Salesforce email exactly, or their leads and rank won't be found"
                          onChange={(e) => setEdit({ ...edit, email: e.target.value })} />
                      </div>
                    </td>
                    <td>
                      <Select size="sm" aria-label="Region" options={regionOptions('—')} value={edit.region}
                        onChange={(e) => setEdit({ ...edit, region: e.target.value })} />
                    </td>
                    <td>
                      <TextInput size="sm" aria-label="Team" value={edit.team}
                        onChange={(e) => setEdit({ ...edit, team: e.target.value })} />
                    </td>
                    <td />
                    <td>
                      <div className="sales-actions sales-actions--end">
                        <Button variant="primary" size="sm"
                          onClick={() => void save(a, {
                            ...(edit.email.trim().toLowerCase() !== a.email && { email: edit.email.trim().toLowerCase() }),
                            name: edit.name,
                            region: edit.region || null,
                            team: edit.team || null,
                          })}>Save</Button>
                        <Button variant="secondary" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={a.id} className={a.isActive ? undefined : 'sales-row--inactive'}>
                    <td>
                      <div className="ds-strong">{a.name}</div>
                      <div className="sales-caption ds-text-muted">{a.email}</div>
                    </td>
                    <td>{a.region ?? '—'}</td>
                    <td>{a.team ?? '—'}</td>
                    <td>{a.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}</td>
                    <td>
                      <div className="sales-actions sales-actions--end">
                        <Button variant="ghost" size="sm"
                          onClick={() => { setEditing(a.id); setEdit({ email: a.email, name: a.name, region: a.region ?? '', team: a.team ?? '' }); }}>Edit</Button>
                        <Button variant="ghost" size="sm"
                          title={a.isActive ? 'Keeps their history; no new targets' : undefined}
                          onClick={() => void save(a, { isActive: !a.isActive })}>
                          {a.isActive ? 'Deactivate' : 'Reactivate'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
};

const SalesAgentsPage: React.FC = () => {
  if (!REPORTS_ENABLED) {
    return (
      <Page>
        <PageHeader eyebrow="Sales" title="Sales Agents" />
        <EmptyState title="Reports aren't enabled" description="Reports aren't enabled in this environment yet." />
      </Page>
    );
  }
  return (
    <Page>
      <PageHeader
        eyebrow="Sales"
        title="Sales Agents"
        subtitle={
          <>
            Who the sales agents are. Set their monthly targets on{' '}
            <Link to={`${process.env.PUBLIC_URL}/pages/sales-targets`}>Targets &amp; Incentives</Link>.
          </>
        }
      />
      <AgentsTab />
    </Page>
  );
};

export default SalesAgentsPage;
